import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Stack,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  useToast,
  useToken,
  Spinner,
  Link,
  SimpleGrid,
  Input,
  Icon,
  Flex,
  Switch,
  Badge,
  Divider,
  Center,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaFilePdf,
  FaTimesCircle,
  FaRocket,
  FaCrown,
  FaBolt,
  FaFire,
} from "react-icons/fa";
import { BsStars } from "react-icons/bs";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "../context/AuthContext";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import svgCreditIcon from "../assets/svg/credits.svg";
import { Image } from "@chakra-ui/react";

// --- LOGIQUE STRIPE ---
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

// --- COMPOSANT MOTION ---
const MotionBox = motion(Box);

const Subscribe = () => {
  const { currentUser, getToken } = useAuth();
  const [userData, setUserData] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);
  const toast = useToast();
  const { t } = useTranslation();

  const [promoCode, setPromoCode] = useState("");
  const [isAnnual, setIsAnnual] = useState(true); // Toggle Annuel/Mensuel

  // --- STYLES & COULEURS (design system) ---
  const bgMain = "background";
  const highlightNeon = "neon";
  const accentColor = "accent.500";
  const primaryColor = "primary.500";
  const textLight = "muted-foreground";

  // --- LOGIQUE API ---

  const fetchUserData = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await getToken();
      const headers: HeadersInit = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch("/.netlify/functions/get-subscription", {
        method: "POST",
        headers,
        body: JSON.stringify({ userId: currentUser.uid }),
      });

      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      } else {
        console.error("Failed to fetch user data", response.status);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [currentUser, getToken]);

  const handlePromoCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch("/.netlify/functions/apply-promo-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: currentUser?.uid, promoCode }),
      });

      const data = await response.json();

      if (response.ok) {
        let description = "";
        let title = t("promoCodeApplied");

        if (data.planUpdated && data.creditsAdded > 0) {
            description = t("creditsAndPlanUpdated", { credits: data.creditsAdded, plan: typeof data.planUpdated === 'string' ? data.planUpdated.toUpperCase() : "" });
        } else if (data.planUpdated) {
            title = t("planUpdatedSuccess");
            description = t("planUpdatedSuccessDesc", { plan: typeof data.planUpdated === 'string' ? data.planUpdated.toUpperCase() : "" });
        } else {
            description = t("creditsAdded", { count: data.creditsAdded });
        }

        toast({
          title,
          description,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setPromoCode("");
        fetchUserData(); // Refresh credits
      } else {
        toast({
          title: t("promoCodeError"),
          description: data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error applying promo code:", error);
      toast({
        title: t("promoCodeError"),
        description: t("unexpectedError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (currentUser) fetchUserData();
  }, [currentUser, fetchUserData]);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoadingInvoices(true);
      try {
        const token = await getToken();
        const headers: HeadersInit = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const response = await fetch("/.netlify/functions/getInvoices", {
          method: "POST",
          headers,
          body: JSON.stringify({ userId: currentUser?.uid }),
        });

        if (response.ok) {
          const data = await response.json();
          setInvoices(data);
        } else {
          console.error("Failed to fetch invoices", response.status);
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setLoadingInvoices(false);
      }
    };

    if (currentUser) fetchInvoices();
  }, [currentUser, getToken]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sessionId = query.get("session_id");
    if (sessionId) {
      toast({
        title: t("paymentSuccessTitle"),
        description: t("paymentSuccessDesc"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Force refresh data immediately after successful payment return
      fetchUserData();
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast, fetchUserData, t]);

  const handlePortalRedirect = async () => {
    try {
      const token = await getToken();
      if (!token) throw new Error("Authentication token missing");

      const response = await fetch("/.netlify/functions/create-portal-session", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ userId: currentUser?.uid }),
      });

      if (!response.ok) throw new Error("Impossible de créer la session portail");

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error("Portal error:", error);
      toast({
        title: t("portalErrorTitle"),
        description: t("portalErrorDesc"),
        status: "error",
      });
    }
  };

  const handleSubscription = async (plan: string) => {
    if (!currentUser || !currentUser.uid) {
      console.error("User is not authenticated or UID is missing.");
      toast({ title: t("error"), description: t("loginRequired"), status: "error" });
      return;
    }

    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe not loaded");

      const token = await getToken();
      if (!token) throw new Error("Authentication token missing");

      const response = await fetch(
        "/.netlify/functions/create-checkout-session",
        {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            plan,
            userId: currentUser.uid,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const { sessionId } = await response.json();

      if (!sessionId) {
        throw new Error("No sessionId returned from server");
      }

      const result = await stripe.redirectToCheckout({ sessionId });

      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (error: any) {
      console.error("Subscription error:", error);
      toast({
        title: t("subscriptionErrorTitle"),
        description: error.message || "Impossible de créer la session de paiement.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp || !timestamp._seconds) {
      return "Date non disponible";
    }
    return format(new Date(timestamp._seconds * 1000), "dd MMM yyyy");
  };

  // --- RENDER HELPERS ---
  const hasActivePlan = userData?.plan && userData.plan !== 'free';

  const PayAsYouGoSection = () => (
    <Box
      maxW="5xl"
      mx="auto"
      bg="linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)"
      borderRadius="2xl"
      p={10}
      border="1px solid"
      borderColor="whiteAlpha.200"
      position="relative"
      overflow="hidden"
      mb={20}
      zIndex={1}
    >
      <Box
        position="absolute"
        top="-50%"
        left="-10%"
        w="300px"
        h="300px"
        bg="purple.500"
        filter="blur(100px)"
        opacity="0.2"
        zIndex={-1}
      />
      
      <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={8} position="relative" zIndex={2}>
        <Box>
          <Flex align="center" mb={2}>
            <Icon as={BsStars} color="#d1fe17" w={6} h={6} mr={2} />
            <Heading size="lg" color="white">
              {hasActivePlan ? t("needToGoFurther") : t("needFlexibility")}
            </Heading>
          </Flex>
          <Text color="gray.400" fontSize="lg" mb={4}>
            {hasActivePlan 
              ? t("payAsYouGoDescActive")
              : t("payAsYouGoDescInactive")
            }
          </Text>
          <HStack spacing={4}>
              <Badge bg="whiteAlpha.200" color="#d1fe17" p={2} borderRadius="md">{t("1000credits")}</Badge>
              <Badge bg="whiteAlpha.200" color="#d1fe17" p={2} borderRadius="md">{t("noCommitmentBadge")}</Badge>
              <Badge bg="whiteAlpha.200" color="#d1fe17" p={2} borderRadius="md">{t("plus1Asin")}</Badge>
          </HStack>
        </Box>

        <Box minW={{ base: "100%", md: "320px" }} className="animated-border-card-wrap">
          <div className="animated-border-card-border" aria-hidden="true" />
          <Box className="animated-border-card-content" textAlign="center">
            <Text fontSize="sm" color="gray.400" mb={2}>{t("oneTimePayment")}</Text>
            <Text fontSize="4xl" fontWeight="bold" color="white" mb={4}>30€</Text>
            <Button
              size="lg"
              width="100%"
              bg="transparent"
              color="#d1fe17"
              border="2px solid"
              borderColor="#d1fe17"
              _hover={{ bg: "neon", color: "background", opacity: 0.9 }}
              onClick={() => handleSubscription("pay-as-you-go")}
            >
              {t("buyCredits")}
            </Button>
          </Box>
        </Box>
      </Flex>
    </Box>
  );

  // --- HELPER FOR LEGACY PLANS ---
  const getCurrentPlanKey = (plan: string) => {
    if (!plan) return null;
    if (plan === "monthly") return "starter_monthly";
    if (plan === "annual") return "pro_annual";
    return plan;
  };

  const currentPlan = getCurrentPlanKey(userData?.plan);

  return (
    <Box
      bg={bgMain}
      minH="100vh"
      py={20}
      px={{ base: 4, md: 8 }}
      color="white"
      position="relative"
      width="100vw"
      left="50%"
      right="50%"
      marginLeft="-50vw"
      marginRight="-50vw"
      marginTop="-60px"
    >
      {/* HEADER */}
      <Stack spacing={4} textAlign="center" mb={16}>
        <Badge
          colorScheme="primary"
          variant="solid"
          alignSelf="center"
          px={4}
          py={1}
          borderRadius="full"
          textTransform="uppercase"
          fontSize="xs"
          letterSpacing="wide"
        >
          BulletMonkey Premium
        </Badge>
        <Heading
          as="h1"
          fontSize={{ base: "3xl", md: "5xl" }}
          fontWeight="extrabold"
          bgGradient="linear(to-r, foreground, muted-foreground)"
          bgClip="text"
        >
          {t("boostSalesTitle")} <br />
          <Text as="span" color={highlightNeon}>
            {t("chooseWeaponTitle")}
          </Text>
        </Heading>
        <Text fontSize="xl" color={textLight} maxW="2xl" mx="auto">
          {userData ? (
            <>
              {t("currentPlan")} <Text as="span" color="white" fontWeight="bold">{currentPlan?.replace('_', ' ').toUpperCase()}</Text>
              {userData.endSubscriptionDate && ` (Fin le ${formatDate(userData.endSubscriptionDate)})`}
              <br />
              {t("creditsRemaining")} <Flex as="span" display="inline-flex" alignItems="center" ml={1}><Image src={svgCreditIcon} boxSize="20px" mr={1} alt="Credits" /><Text as="span" color={highlightNeon} fontWeight="bold">{userData.credits || 0}</Text></Flex>
            </>
          ) : (
            "Des outils d'IA avancés pour transformer des visiteurs curieux en acheteurs compulsifs."
          )}
        </Text>
      </Stack>

      {/* Pay As You Go - Rendered TOP if user has active plan */}
      {hasActivePlan && <PayAsYouGoSection />}

      {/* SWITCH ANNUEL / MENSUEL */}
      <Flex justify="center" align="center" mb={12} mt={hasActivePlan ? 0 : -8} gap={4}>
        <Text
          fontWeight="bold"
          color={!isAnnual ? "white" : textLight}
          cursor="pointer"
          onClick={() => setIsAnnual(false)}
        >
          {t("monthly")}
        </Text>
        <Switch
          size="lg"
          colorScheme="primary"
          isChecked={isAnnual}
          onChange={() => setIsAnnual(!isAnnual)}
          sx={{
            "span.chakra-switch__track": {
              bg: "muted",
            },
            "span.chakra-switch__track[data-checked]": {
              bg: highlightNeon,
            },
          }}
        />
        <Text
          fontWeight="bold"
          color={isAnnual ? "white" : textLight}
          cursor="pointer"
          onClick={() => setIsAnnual(true)}
        >
          {t("annual")}{" "}
          <Badge ml={2} colorScheme="primary" variant="solid" bg={highlightNeon} color="background">
            {t("save40percent")}
          </Badge>
        </Text>
      </Flex>

      {/* PLANS GRID */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} maxW="7xl" mx="auto" mb={20}>
        
        {/* PLAN 1: STARTER */}
        <PricingCard
          title={t("planStarterTitle")}
          description={t("planStarterDesc")}
          price={isAnnual ? 39 : 50}
          annualPrice={isAnnual ? 50 : null}
          isAnnual={isAnnual}
          icon={FaRocket}
          accentColor={primaryColor}
          features={[
            { text: t("feature1000Credits"), bold: true, check: true },
            { text: t("featureUnlimitedAccess"), check: true },
            { text: t("feature3AsinMonth"), check: true },
            { text: t("featurePrioritySupport"), check: true },
            { text: t("featureMonkeyRoast"), check: false },
          ]}
          buttonText={currentPlan === (isAnnual ? "starter_annual" : "starter_monthly") ? t("manageMyPlan") : t("chooseStarter")}
          onClick={
            currentPlan === (isAnnual ? "starter_annual" : "starter_monthly") 
              ? handlePortalRedirect 
              : () => handleSubscription(isAnnual ? "starter_annual" : "starter_monthly")
          }
          isCurrentPlan={currentPlan === (isAnnual ? "starter_annual" : "starter_monthly")}
          isActive={true} 
        />

        {/* PLAN 2: PRO */}
        <PricingCard
          title={t("planProTitle")}
          description={t("planProDesc")}
          price={isAnnual ? 29 : 49}
          annualPrice={isAnnual ? 49 : null}
          isAnnual={isAnnual}
          icon={FaBolt}
          accentColor={highlightNeon}
          isPopular
          isFeatured={!hasActivePlan && !isAnnual}
          features={[
            { text: t("feature2500Credits"), bold: true, check: true },
            { text: t("featureAllStarter"), check: true },
            { text: t("featureUnlimitedAsin"), check: true },
            { text: t("featureUnlimitedTranslations"), check: true },
            { text: t("featurePremiumSupport"), check: true },
            { text: t("featureSaveAnnual"), check: true, highlight: isAnnual },
          ]}
          buttonText={currentPlan === (isAnnual ? "pro_annual" : "pro_monthly") ? t("manageMyPlan") : t("choosePro")}
          onClick={
            currentPlan === (isAnnual ? "pro_annual" : "pro_monthly")
              ? handlePortalRedirect
              : () => handleSubscription(isAnnual ? "pro_annual" : "pro_monthly")
          }
          isCurrentPlan={currentPlan === (isAnnual ? "pro_annual" : "pro_monthly")}
          isActive={true}
        />

        {/* PLAN 3: EMPIRE */}
        <PricingCard
          title={t("planEmpireTitle")}
          description={t("planEmpireDesc")}
          price={isAnnual ? 99 : 149}
          annualPrice={isAnnual ? 149 : null}
          isAnnual={isAnnual}
          icon={FaCrown}
          accentColor={accentColor}
          features={[
            { text: t("featureOnQuote"), bold: true, check: true },
            { text: t("featureAllPro"), check: true },
            { text: t("featureAgentModel"), check: true },
            { text: t("featureMonkeyRoastAudit"), check: true, highlight: true, icon: FaFire },
            { text: t("featureExportFba"), check: true },
            { text: t("featureDedicatedSupport"), check: true },
          ]}
          buttonText={currentPlan === (isAnnual ? "empire_annual" : "empire_monthly") ? t("manageMyPlan") : t("chooseEmpire")}
          onClick={
            currentPlan === (isAnnual ? "empire_annual" : "empire_monthly")
              ? handlePortalRedirect
              : () => handleSubscription(isAnnual ? "empire_annual" : "empire_monthly")
          }
          isCurrentPlan={currentPlan === (isAnnual ? "empire_annual" : "empire_monthly")}
          isActive={true}
        />
      </SimpleGrid>

      {/* Pay As You Go - Rendered BOTTOM if user has NO active plan */}
      {!hasActivePlan && <PayAsYouGoSection />}

      <Divider borderColor="whiteAlpha.200" mb={10} />

      {/* FOOTER : PROMO CODE & HISTORY */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} maxW="7xl" mx="auto">
        
        {/* Code Promo */}
        <Box>
            <Heading as="h3" size="md" mb={4}>Code Promo</Heading>
            <form onSubmit={handlePromoCodeSubmit}>
            <HStack>
                <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder={t("enterPromoCode")}
                bg="gray.800"
                border="none"
                _focus={{ bg: "muted" }}
                />
                <Button type="submit" colorScheme="primary">
                {t("apply")}
                </Button>
            </HStack>
            </form>
        </Box>

        {/* Historique */}
        <Box>
            <Heading as="h3" size="md" mb={4}>{t("billingHistory")}</Heading>
            {loadingInvoices ? (
            <Spinner />
            ) : invoices.length > 0 ? (
            <Stack spacing={4} maxH="300px" overflowY="auto">
                {invoices.map((invoice) => (
                <Box
                    key={invoice.id}
                    p={4}
                    bg="gray.800"
                    borderRadius="md"
                    position="relative"
                >
                    <Text fontSize="sm">
                    <strong>{t("date")}</strong> {formatDate(invoice.created * 1000)}
                    </Text>
                    <Text fontSize="sm">
                    <strong>{t("amount")}</strong> {invoice.amount_paid / 100}{" "}
                    {invoice.currency.toUpperCase()}
                    </Text>
                    <Badge colorScheme={invoice.status === 'paid' ? 'green' : 'red'}>{invoice.status}</Badge>
                    {invoice.invoice_pdf && (
                    <Link
                        href={invoice.invoice_pdf}
                        isExternal
                        position="absolute"
                        right={4}
                        top={4}
                    >
                        <Icon as={FaFilePdf as any} boxSize="20px" color="gray.400" _hover={{ color: "white" }} />
                    </Link>
                    )}
                </Box>
                ))}
            </Stack>
            ) : (
            <Text color="gray.500">{t("noInvoicesAvailable")}</Text>
            )}
        </Box>
      </SimpleGrid>

      <Center mt={16}>
        <Text color="gray.500" fontSize="sm">
          {t("securePaymentFooter")}
        </Text>
      </Center>
    </Box>
  );
};

// --- SUB-COMPONENT: PRICING CARD ---

interface PricingCardProps {
  title: string;
  description: string;
  price: number;
  annualPrice: number | null;
  isAnnual: boolean;
  icon: any;
  accentColor: string;
  isPopular?: boolean;
  isFeatured?: boolean;
  features: { text: string; check: boolean; bold?: boolean; highlight?: boolean; icon?: any }[];
  buttonText: string;
  onClick: () => void;
  isCurrentPlan: boolean;
  isActive: boolean;
}

const PricingCard = ({
  title,
  description,
  price,
  annualPrice,
  isAnnual,
  icon,
  accentColor,
  isPopular,
  isFeatured,
  features,
  buttonText,
  onClick,
  isCurrentPlan,
  isActive
}: PricingCardProps) => {
  const opacity = isActive ? 1 : 0.5;
  const scale = isActive ? 1 : 0.95;
  const { t } = useTranslation();
  const resolvedGlowColor = useToken("colors", accentColor) ?? accentColor;
  const currentPlanCyan = "#00d4ff";
  const hasCatchyStyle = isCurrentPlan || isFeatured;

  const effectiveAccentColor = isCurrentPlan ? currentPlanCyan : accentColor;
  const catchyClassName = isCurrentPlan
    ? "animated-border-card-wrap animated-border-card-wrap--cyan"
    : isFeatured
    ? "animated-border-card-wrap"
    : undefined;

  return (
    <MotionBox
      animate={{ opacity, scale }}
      whileHover={isActive ? { y: -10 } : {}}
      transition={{ duration: 0.3 }}
      className={catchyClassName}
      bg={hasCatchyStyle ? "transparent" : "card"}
      borderRadius="2xl"
      border={hasCatchyStyle ? "none" : isPopular ? "1px solid" : "none"}
      borderColor={!hasCatchyStyle && isPopular ? accentColor : undefined}
      position="relative"
      overflow="hidden"
      boxShadow={hasCatchyStyle ? "none" : (isPopular && isActive ? `0 0 40px -10px ${resolvedGlowColor}` : "none")}
    >
      {hasCatchyStyle && <div className="animated-border-card-border" aria-hidden="true" />}
      <Box className={hasCatchyStyle ? "animated-border-card-content" : undefined} position="relative" zIndex={2}>
      {isPopular && !isCurrentPlan && (
        <Box
          bg={accentColor}
          color="background"
          fontWeight="bold"
          fontSize="xs"
          textAlign="center"
          py={1}
          position="absolute"
          top={0}
          left={0}
          right={0}
        >
          {t("bestOffer")}
        </Box>
      )}

      {isCurrentPlan && (
        <Box
          bg={currentPlanCyan}
          color="background"
          fontWeight="bold"
          fontSize="xs"
          textAlign="center"
          py={1}
          position="absolute"
          top={0}
          left={0}
          right={0}
        >
          {t("currentPlanBadge")}
        </Box>
      )}

      <Stack p={8} spacing={6} pt={(isPopular || isCurrentPlan || isFeatured) ? 12 : 8}>
        {/* Header Carte */}
        <Flex justify="space-between" align="start">
          <Box>
            <Heading size="md" mb={2} color="white">
              {title}
            </Heading>
            <Text fontSize="sm" color="gray.400" minH="40px">
              {description}
            </Text>
          </Box>
          <Icon as={icon} w={8} h={8} color={effectiveAccentColor} />
        </Flex>

        {/* Prix */}
        <Box>
          <Flex align="baseline">
            <Text fontSize="5xl" fontWeight="extrabold" color="white">
              {price}€
            </Text>
            <Text fontSize="xl" fontWeight="medium" color="gray.500" ml={1}>
              {t("perMonth")}
            </Text>
          </Flex>
          {isAnnual && annualPrice && (
            <Text
              fontSize="sm"
              color="gray.500"
              textDecoration="line-through"
              mt={1}
            >
              {annualPrice}€ {t("perMonthBilledMonthly")}
            </Text>
          )}
          <Text fontSize="xs" color={effectiveAccentColor} mt={2} fontWeight="bold">
            {isAnnual ? t("billedPerYear", { amount: price * 12 }) : t("noCommitment")}
          </Text>
        </Box>

        {/* Bouton Action */}
        <Button
          size="lg"
          bg={isCurrentPlan ? "whiteAlpha.300" : (isPopular ? accentColor : "whiteAlpha.200")}
          color={isCurrentPlan ? "white" : (isPopular ? "black" : "white")}
          _hover={isActive ? {
            bg: isPopular ? "white" : "whiteAlpha.300",
            transform: "scale(1.05)",
          } : {}}
          onClick={onClick}
          isDisabled={false} // Toujours activé pour permettre "Gérer" ou "Choisir"
          border={isCurrentPlan ? `1px solid ${currentPlanCyan}` : "none"}
        >
          {buttonText}
        </Button>

        <Divider borderColor="whiteAlpha.200" />

        {/* Features */}
        <List spacing={4}>
          {features.map((feature, idx) => (
            <ListItem key={idx} display="flex" alignItems="center" color={feature.check ? "gray.300" : "gray.600"}>
              <ListIcon
                as={feature.check ? FaCheckCircle : FaTimesCircle}
                color={feature.check ? (feature.highlight ? effectiveAccentColor : "white") : "muted-foreground"}
                mr={3}
              />
              <Text
                fontWeight={feature.bold ? "bold" : "normal"}
                color={feature.bold ? "white" : "inherit"}
                bg={feature.highlight ? "whiteAlpha.100" : "transparent"}
                px={feature.highlight ? 2 : 0}
                py={feature.highlight ? 1 : 0}
                borderRadius={feature.highlight ? "md" : "none"}
                display="flex"
                alignItems="center"
              >
                {feature.icon && <Icon as={feature.icon} color="orange.400" mr={2} />}
                {feature.text}
              </Text>
            </ListItem>
          ))}
        </List>
      </Stack>
      </Box>
    </MotionBox>
  );
};

export default Subscribe;